import resource from '../../../share/util/resourceMap';
import apiService from '../../../share/util/api/service';
import logWrapper from '../../../share/logWrapper';
import { executePolling, postResponseCheckDefaultAction, REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../share/util/api/polling';
import { executeSyncRequest } from '../../../share/util/api/syncRequest';
import { TRANSITION, VEHICLE_DESTINATION_GET, DESTINATION_TYPE, VEHICLE_DESTINATION_SAVE, VEHICLE_DESTINATION_SUPPORT_IDS, DESTINATION_PHASE } from './constants';
import { getAbbrSysName } from '../../../share/util/systemName';
const DiagSystemUtils = require('../../../common/nodejs/diagSystemUtils');

const CST_DESTINATION_LIST = ['00', '03', '04', '05', '06', '07', '0E', '0F'];

/**
 * 読み出したデータから仕向け地の判定
 * @param {String} readData 読み出したデータ
 * @param {String} systemId システムId
 * @param route 呼び出し元のオブジェクト(this)
 */
export const readSetDestination = (readData, systemId, route) => {

  let destination = readData;
  if (!CST_DESTINATION_LIST.includes(destination)) {
    destination = '02';
  }
  // 仕向け地のためデータ型はHEX固定
  return resource.convertCstmDisplayItem('1148_1_all', destination, systemId, route, 'HEX');
};

/**
 * 入力コードからDataRecordを作成
 * @param {String} inputCode 入力されたコード
 */
export const writeSetDestination = (inputCode) => {

  const code = inputCode.toUpperCase();

  if (code.startsWith('JP')) {
    return '00';
  } else if (code.startsWith('KA') || code.startsWith('KC')) {
    return '04';
  } else if (code.startsWith('KS')) {
    return '06';
  } else if (code.startsWith('C0') || code.startsWith('C5')) {
    return '0F';
  } else if (code.startsWith('U4') || code.startsWith('U5') || code.startsWith('U6')) {
    return '0E';
  } else if (code.startsWith('EK') || code.startsWith('ER') || code.startsWith('EN')) {
    return '03';
  } else if (code.startsWith('K4')) {
    return '05';
  } else if (code.startsWith('C6')) {
    return '07';
  } else {
    return '02';
  }
};

/**
 * 仕向地設定モーダルのヘッダタイトル取得
 * @param {string} destinationPhase 仕向地設定のphase
 */
export const getDestinationModalTitle = (root, destinationPhase) => {
  // 仕向地変更確認画面, 仕向地コード入力画面
  const DestinationTitle = 'individual.header_cst_set_destination';
  // 仕向地設定再確認画面
  const WarningTitle = 'message.common_title_warning';
  const titleMap = {
    confirmation: DestinationTitle,
    inputCode: DestinationTitle,
    reconfirmation: WarningTitle
  };
  return root.$t(titleMap[destinationPhase]);
};

/**
 * 仕向地設定モーダルのヘッダカラー
 */
export const getDestinationHeaderColor = (destinationPhase) => {
  switch (destinationPhase) {
    case DESTINATION_PHASE.RECONFIRMATION:
      return "warning";
    case DESTINATION_PHASE.CONFIRMATION:
    case DESTINATION_PHASE.INPUT_CODE:
    default:
      return "secondary";
  }
};

/**
 * 仕向地設定モーダルの表示文言
 */
export const getDestinationMessage = (root, confirmationDestination, reconfirmationDestination, destinationPhase) => {
  const messageMap = {
    confirmation: confirmationDestination,
    inputCode: 'individual.label_cst_input_destination',
    reconfirmation: reconfirmationDestination
  };
  switch (destinationPhase) {
    case DESTINATION_PHASE.INPUT_CODE:
      return root.$t(messageMap[destinationPhase]);
    case DESTINATION_PHASE.CONFIRMATION:
    case DESTINATION_PHASE.RECONFIRMATION:
      return messageMap[destinationPhase];
    default:
      return '';
  }
};

/**
 * 入力欄表示判定
 * @param {string} destinationPhase 仕向地設定のphase
 * @returns {boolean} true: 表示
 */
export const isShowInputForm = (destinationPhase) => {
  const showInputCodeMap = {
    inputCode: true
  };
  return !!showInputCodeMap[destinationPhase];
};

// パターン(ASCII文字)
const patternASCII = new RegExp(/^[\x21-\x7e\s]*$/);
/**
 * 仕向地コードの検証
 * @param {object} root 呼び出し元
 * @param {string} destinationCode 仕向地コード
 * @returns {object?} 検証結果
 */
export const isDestinationInputCodeValidate = (root, destinationCode) => {
  // 入力規則(制御文字を除くASCII文字)
  if (!patternASCII.test(destinationCode)) {
    // 入力規則に沿っていない場合はアラートを表示する
    return { valid: true, msg: root.$t(`message.alert_validate_invalid_pattern`) };
  }
  return undefined;
};

// パターン(英字のみ)
const patternEn = new RegExp(/^[a-zA-Z]+$/);
/**
 * 仕向地コードの入力内容チェック
 * @param {string} destinationCode 仕向地コード
 * @returns {boolean} チェック結果 true:1文字目が英字ではない または 2文字未満
 */
export const checkDestinationCodeDetail = (destinationCode) => {
  // 入力内容(1文字目)
  const input1stChar = destinationCode.slice(0, 1);
  return !patternEn.test(input1stChar) || destinationCode.length < 2;
};

/**
 * ECUリストに診断システムID略称がボディコントロールユニット（BIU）のシステムが含まれるかチェック
 * @param {string[]} systemList システム名のリスト 
 * @returns {string?} 含まれる場合はシステム名を返す
 */
export const checkBIU = (systemList) => {
  return systemList.find((sys) => DiagSystemUtils.isBodyControlByAbbreviationId(getAbbrSysName(sys)));
};

/**
 * サポートDID読出し
 * @param {object} root 呼び出し元
 * @param {string} root.tmpRequestId 使用したrequestIdを設定するプロパティ
 * @param {string} systemId システム名
 * @param {boolean} isUseCache キャッシュフラグ
 * @returns {Promise<object?>} 実行結果 失敗ならnull
 */
export const readSupportDid = async (root, systemId, isUseCache = false) =>
  await executePolling(
    root,
    // POST API 呼び出しのコールバック
    async () => {
      return await apiService.postCall(
        apiService.CST,
        `/netApps/${root.netAppId}/systems/${systemId}/customize/destinationSetting`,
        {
          type: DESTINATION_TYPE.SUPPORT_DID,
          use_cache: isUseCache
        }
      );
    },
    // GET API 呼び出しのコールバック
    async (postApiResponse) => {
      return await apiService.getCall(
        apiService.CST,
        `/netApps/${root.netAppId}/systems/${systemId}/customize/requests/${postApiResponse.data.request_id}`
      );
    },
    // エラー発生のコールバック
    (errorType) => {
      logWrapper.log("errorCallback called.", errorType, true);
      root.showUnexpectedError(VEHICLE_DESTINATION_SUPPORT_IDS);
    },
    // POST API レスポンスチェックのコールバック
    (postApiResponse) => {
      const result = postResponseCheckDefaultAction(postApiResponse);
      if (!result) {
        // リクエストID が正常
        // 次処理で使用したいためrequest_idを控える
        root.tmpRequestId = postApiResponse.data.request_id;
      }
      return result;
    },
    // GET API レスポンスチェックのコールバックはデフォルト処理を使用
    undefined,
    // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
    (result) => { root.validateErrorResult(result, VEHICLE_DESTINATION_SUPPORT_IDS); },
    // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
    (result) => { root.validateErrorResult(result, VEHICLE_DESTINATION_SUPPORT_IDS); },
  );

/**
 * メニューサポート取得
 * 呼び出し元に使用したrequestIdを設定
 * @param {object} root 呼び出し元
 * @param {string} root.tmpRequestId 使用したrequestIdを設定するプロパティ
 * @param {string} systemId システム名
 * @returns {Promise<object?>} 実行結果 失敗ならnull
 */
export const getMenuSupport = async (root, systemId) =>
  await executePolling(
    root,
    // POST API 呼び出しのコールバック
    async () => await apiService.postCall(
      apiService.CST,
      `/netApps/${root.netAppId}/systems/${systemId}/customize/destinationSetting`,
      {
        type: DESTINATION_TYPE.READ_MENU
      }
    ),
    // GET API 呼び出しのコールバック
    async (postApiResponse) => await apiService.getCall(
      apiService.CST,
      `/netApps/${root.netAppId}/systems/${systemId}/customize/requests/${postApiResponse.data.request_id}`
    ),
    // エラー発生のコールバック
    (errorType) => { errorType; root.showUnexpectedError(VEHICLE_DESTINATION_GET); },
    // POST API レスポンスチェックのコールバック
    (postApiResponse) => {
      const result = postResponseCheckDefaultAction(postApiResponse);
      if (!result) {
        // リクエストID が正常
        // 次処理で使用したいためrequest_idを控える
        root.tmpRequestId = postApiResponse.data.request_id;
      }
      return result;
    },
    // GET API レスポンスチェックのコールバックはデフォルト処理を使用
    undefined,
    // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
    (result) => { root.validateErrorResult(result, VEHICLE_DESTINATION_GET); },
    // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
    (result) => { root.validateErrorResult(result, VEHICLE_DESTINATION_GET); },
  );

/**
 * [UI.EXE.CST.RD.BIU.1]の実行条件を満たしているか確認する
 * @param {object} root 呼び出し元
 * @param {string} systemId 現在のECU名
 * @returns {Promise<object?>} 実行結果 失敗ならnull
 */
export const getMatchCondition = async (root, systemId, requestId) =>
  await executeSyncRequest(
    root,
    async () => {
      return await apiService.getParamCall(
        apiService.CST,
        `/netApps/${root.netAppId}/systems/${systemId}/customize/destinationSetting/${requestId}`,
        {
          type: DESTINATION_TYPE.READ_MENU
        }
      );
    },
    // エラー発生のコールバック
    (errorType) => {
      logWrapper.log("errorCallback called.", errorType, true);
      root.showUnexpectedError(VEHICLE_DESTINATION_GET);
    },
    // GET API で正常レスポンスが返ってきた際の内容チェックはデフォルト処理を利用
    undefined,
    // API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
    (result) => { root.validateErrorResult(result, VEHICLE_DESTINATION_GET); },
  );

/**
 * 仕向地設定の現在設定値を取得
 * 呼び出し元に使用したrequestIdを設定
 * @param {object} root 呼び出し元
 * @param {string} root.tmpRequestId 使用したrequestIdを設定するプロパティ
 * @param {string} systemId システム名
 * @returns {Promise<object?>} 実行結果 失敗ならnull
 */
export const readDestinationSetting = async (root, systemId) =>
  await executePolling(
    root,
    // POST API 呼び出しのコールバック
    async () => {
      return await apiService.postCall(
        apiService.CST,
        `/netApps/${root.netAppId}/systems/${systemId}/customize/destinationSetting`,
        {
          type: DESTINATION_TYPE.READ
        }
      );
    },
    // GET API 呼び出しのコールバック
    async (postApiResponse) => {
      return await apiService.getCall(
        apiService.CST,
        `/netApps/${root.netAppId}/systems/${systemId}/customize/requests/${postApiResponse.data.request_id}`
      );
    },
    // エラー発生のコールバック
    (errorType) => {
      logWrapper.log("errorCallback called.", errorType, true);
      root.showUnexpectedError(VEHICLE_DESTINATION_GET);
    },
    // POST API レスポンスチェックのコールバック
    (postApiResponse) => {
      const result = postResponseCheckDefaultAction(postApiResponse);
      if (!result) {
        // リクエストID が正常
        // 次処理で使用したいためrequest_idを控える
        root.tmpRequestId = postApiResponse.data.request_id;
      }
      return result;
    },
    // GET API レスポンスチェックのコールバックはデフォルト処理を使用
    undefined,
    // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
    (result) => { root.validateErrorResult(result, VEHICLE_DESTINATION_GET); },
    // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
    (result) => { root.validateErrorResult(result, VEHICLE_DESTINATION_GET); },
  );

/**
 * 仕向地設定の現在設定値取得の結果取得
 * @param {object} root 呼び出し元
 * @param {string} systemId システム名
 * @param {string} requestId リクエストID
 * @returns {Promise<object?>} 実行結果 失敗ならnull
 */
export const getCurrentSettingValue = async (root, systemId, requestId) =>
  await executeSyncRequest(
    root,
    async () => {
      return await apiService.getParamCall(
        apiService.CST,
        `/netApps/${root.netAppId}/systems/${systemId}/customize/destinationSetting/${requestId}`,
        {
          type: DESTINATION_TYPE.READ
        }
      );
    },
    // エラー発生のコールバック
    (errorType) => {
      logWrapper.log("errorCallback called.", errorType, true);
      root.showUnexpectedError(VEHICLE_DESTINATION_GET);
    },
    undefined,
    // API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
    (result) => { root.validateErrorResult(result, VEHICLE_DESTINATION_GET); },
  );

/**
 * 仕向地設定の現在設定値取得の結果取得の値チェック
 * @param {object?} response
 */
export const checkResCurrentSettingValue = (response) => {
  const res = { status: null };
  if (!response) return res;
  switch (response.data.status) {
    case REQUEST_STATUS_COMPLETED:
      switch (response.data.user_data.transition) {
        case TRANSITION.TRANSITION_CONFIRMATION:
          res.status = true;
          res.transition = TRANSITION.TRANSITION_CONFIRMATION;
          res.destination = response.data.user_data.destination;
          break;
        case TRANSITION.TRANSITION_GET_INFORMATION:
          res.status = true;
          res.transition = TRANSITION.TRANSITION_GET_INFORMATION;
          break;
        default:
          res.status = false;
          break;
      }
      break;
    case REQUEST_STATUS_FAILED:
    default:
      res.status = false;
      break;
  }
  return res;
};

/**
 * 入力した仕向地コードを書き込む
 * @param {object} root 呼び出し元
 * @param {string} systemId システム名
 * @param {string} destinationCode 仕向地コード
 * @returns {Promise<object?>} 実行結果 失敗ならnull
 */
export const writeDestinationSetting = async (root, systemId, destinationCode) => {
  // 仕向地コードからDataRecordを作成
  const writeData = writeSetDestination(destinationCode);
  return await executePolling(
    root,
    // POST API 呼び出しのコールバック
    async () => {
      return await apiService.postCall(
        apiService.CST,
        `/netApps/${root.netAppId}/systems/${systemId}/customize/destinationSetting`,
        {
          type: DESTINATION_TYPE.WRITE,
          input_code: writeData
        }
      );
    },
    // GET API 呼び出しのコールバック
    async (postApiResponse) => {
      return await apiService.getCall(
        apiService.CST,
        `/netApps/${root.netAppId}/systems/${systemId}/customize/requests/${postApiResponse.data.request_id}`
      );
    },
    // エラー発生のコールバック
    (errorType) => {
      logWrapper.log("errorCallback called.", errorType, true);
      root.showUnexpectedError(VEHICLE_DESTINATION_SAVE);
    },
    // POST API レスポンスチェックのコールバック
    (postApiResponse) => {
      const result = postResponseCheckDefaultAction(postApiResponse);
      if (!result) {
        // リクエストID が正常
        // 次処理で使用したいためrequest_idを控える
        root.tmpRequestId = postApiResponse.data.request_id;
      }
      return result;
    },
    // GET API レスポンスチェックのコールバックはデフォルト処理を使用
    undefined,
    // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
    (result) => { root.validateErrorResult(result, VEHICLE_DESTINATION_SAVE); },
    // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
    (result) => { root.validateErrorResult(result, VEHICLE_DESTINATION_SAVE); },
  );
};

/**
 * 仕向地設定の現在設定値更新の結果取得
 * @param {object} root 呼び出し元
 * @param {string} systemId システム名
 * @param {string} requestId リクエストID
 * @returns {Promise<object?>} 実行結果 失敗ならnull
 */
export const getWriteDestinationSetting = async (root, systemId, requestId) =>
  await executeSyncRequest(
    root,
    async () => {
      return await apiService.getParamCall(
        apiService.CST,
        `/netApps/${root.netAppId}/systems/${systemId}/customize/destinationSetting/${requestId}`,
        {
          type: DESTINATION_TYPE.WRITE
        }
      );
    },
    // エラー発生のコールバック
    (errorType) => {
      logWrapper.log("errorCallback called.", errorType, true);
      root.showUnexpectedError(VEHICLE_DESTINATION_SAVE);
    },
    undefined,
    // API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
    (result) => { root.validateErrorResult(result, VEHICLE_DESTINATION_SAVE); },
  );

export default {
  readSetDestination,
  writeSetDestination,
  readSupportDid,
  getMenuSupport,
  getMatchCondition,
  readDestinationSetting,
  getCurrentSettingValue,
  writeDestinationSetting,
  getWriteDestinationSetting,
  checkResCurrentSettingValue
};